import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, ChevronDown, GraduationCap, BadgeCheck, Cpu, Download } from "lucide-react";

const ROLES = [
  {
    id: "pedder",
    company: "Pedder Hyundai",
    title: "Automotive Lot Porter",
    period: "Mar 2026 – Present",
    location: "Poway, CA",
    bullets: [
      "Maintain front-line sales lot and active vehicle inventory to ensure complete operational readiness and visual standards.",
      "Manage physical staging, battery health monitoring, fuel levels, and logistical transfer tracking for incoming and outgoing inventory.",
    ],
    skills: ["Vehicle Inventory Staging", "Battery Health Monitoring", "Logistical Transfer Tracking"],
  },
  {
    id: "sdge",
    company: "San Diego Gas & Electric",
    title: "Tool Repair Administration Associate",
    period: "Feb 2025 – Sep 2025",
    location: null,
    bullets: [
      "Managed tool and equipment triage, logging repair, replacement, and calibration workflows into internal asset tracking systems.",
      "Handled direct procurement of specialized replacement components, parts, and repair services.",
      "Oversaw outgoing and incoming logistics for vendor repairs and precision tool calibration.",
    ],
    skills: ["Asset Tracking Systems", "Direct Procurement", "Vendor Repair Logistics", "Tool Calibration"],
  },
  {
    id: "excimer",
    company: "Excimer Laser Repair Corp.",
    title: "Material Requirement Planner",
    period: "2017 – 2025",
    location: null,
    bullets: [
      "Evaluated historic consumption trends and part usage rates to accurately forecast future demand and establish optimal safety stock thresholds.",
      "Monitored master data and stock levels across active production cycles, aligning raw material availability with manufacturing output.",
      "Collaborated directly with external suppliers and internal purchasing to execute purchase orders and guarantee on-time delivery.",
      "Designed and implemented rigorous inventory control procedures, standard operating policies, and cycle count protocols.",
      "Assisted in the deployment and database configuration of corporate ERP services (Misys) to bridge operational tracking with financial systems.",
    ],
    skills: ["Demand Forecasting", "Safety Stock Optimization", "Master Data & BOM Control", "Purchase Orders & Suppliers", "Cycle Count Protocols", "ERP (Misys) Deployment"],
  },
];

const PROFICIENCIES = [
  "Inventory Auditing",
  "BOM Structuring",
  "Cycle Counting",
  "Supply Chain Optimization",
  "ERP / MRP Software Implementation",
];

const SIDE_CARDS = [
  {
    icon: GraduationCap,
    title: "Education",
    lines: [
      "Miramar Community College — Electronic Music Engineering · 2010–2012",
      "Poway High School — 4 Years Automotive Mechanic Vocational · 2006–2010",
    ],
  },
  {
    icon: BadgeCheck,
    title: "Certifications",
    lines: ["Forklift Certification", "Google Analytics Certification (2020)", "HubSpot Content Marketing Certification (2021)"],
  },
  {
    icon: Cpu,
    title: "Systems & Software",
    lines: ["ERP Systems (Misys)", "QuickBooks", "API Data Integration", "Custom HTML Intranet Reporting", "Inventory App Architecture"],
  },
];

export default function Resume() {
  const [openId, setOpenId] = useState("excimer");

  return (
    <section id="resume" className="relative py-24 sm:py-32 bg-[#0D1320]/60">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <motion.div
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7 }}
          >
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#00F0FF]">
              02 — Experience &amp; Resume
            </p>
            <h2 className="mt-4 font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.1]">
              Hands on the floor, eyes on the data.
            </h2>
            <p className="mt-5 max-w-2xl text-base sm:text-lg text-[#9CA3AF] leading-relaxed">
              Eight years of material planning and inventory control, plus daily
              logistics and asset-tracking operations — select a role to expand it.
            </p>
          </motion.div>
          <motion.a
            href="/Robert-Daniels-Resume.pdf"
            download="Robert-Daniels-Resume.pdf"
            data-testid="resume-download-pdf"
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.98 }}
            className="shrink-0 inline-flex items-center gap-2.5 font-display font-semibold text-[#F9FAFB] border border-white/20 hover:border-[#00F0FF]/70 hover:bg-white/5 rounded-full px-6 py-3.5 transition-all duration-300"
          >
            <Download size={17} />
            Download PDF Resume
          </motion.a>
        </div>

        <div className="mt-10 flex flex-wrap gap-2.5">
          {PROFICIENCIES.map((p, i) => (
            <motion.span
              key={p}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.16em] text-[#00F0FF] border border-[#00F0FF]/25 bg-[#00F0FF]/[0.05] rounded-full px-4 py-2"
            >
              {p}
            </motion.span>
          ))}
        </div>

        <div className="mt-14 relative">
          <div
            className="absolute left-[7px] sm:left-[9px] top-2 bottom-2 w-px bg-gradient-to-b from-[#00F0FF]/60 via-[#2563EB]/40 to-transparent"
            aria-hidden="true"
          />
          <div className="space-y-10">
            {ROLES.map((role, i) => {
              const open = openId === role.id;
              return (
                <motion.article
                  key={role.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.6, delay: i * 0.05 }}
                  className="relative pl-9 sm:pl-12"
                >
                  <span
                    className={`absolute left-0 top-2 h-[15px] w-[15px] rounded-full border-2 transition-colors duration-300 ${
                      open ? "border-[#00F0FF] bg-[#00F0FF]/25" : "border-[#2563EB] bg-[#0B0F17]"
                    }`}
                  />
                  <div
                    className={`sheen rounded-2xl border bg-[#111827]/70 backdrop-blur transition-colors duration-500 ${
                      open ? "border-[#00F0FF]/30" : "border-white/[0.07] hover:border-white/20"
                    }`}
                  >
                    <button
                      data-testid={`resume-card-${role.id}`}
                      onClick={() => setOpenId(open ? null : role.id)}
                      aria-expanded={open}
                      className="w-full text-left p-6 sm:p-8"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2">
                        <div>
                          <h3 className="font-display font-bold text-xl sm:text-2xl text-white tracking-tight">
                            {role.company}
                          </h3>
                          <p className="mt-1 text-[#00F0FF] font-medium">{role.title}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-[#D1D5DB] border border-white/10 bg-white/[0.04] rounded-full px-3.5 py-1.5">
                            {role.period}
                          </span>
                          <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.3 }}>
                            <ChevronDown size={18} className="text-[#6B7280]" />
                          </motion.span>
                        </div>
                      </div>
                      {role.location && (
                        <p className="mt-3 flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.16em] text-[#6B7280]">
                          <MapPin size={12} /> {role.location}
                        </p>
                      )}
                    </button>

                    <AnimatePresence initial={false}>
                      {open && (
                        <motion.div
                          data-testid={`resume-detail-${role.id}`}
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                          className="overflow-hidden"
                        >
                          <div className="px-6 sm:px-8 pb-7">
                            <ul className="space-y-2.5 border-t border-white/[0.07] pt-5">
                              {role.bullets.map((b, bi) => (
                                <li key={bi} className="flex gap-3 text-sm text-[#9CA3AF] leading-relaxed">
                                  <span className="mt-[7px] h-1 w-1 shrink-0 rotate-45 bg-[#00F0FF]" />
                                  {b}
                                </li>
                              ))}
                            </ul>
                            <div className="flex flex-wrap gap-2 pt-5">
                              {role.skills.map((s) => (
                                <span
                                  key={s}
                                  className="text-[11px] text-[#D1D5DB] bg-white/[0.05] border border-white/[0.08] rounded-full px-3 py-1.5"
                                >
                                  {s}
                                </span>
                              ))}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </div>

        <div className="mt-14 grid sm:grid-cols-3 gap-6">
          {SIDE_CARDS.map((c, i) => (
            <motion.div
              key={c.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="h-full rounded-2xl border border-white/[0.07] bg-[#111827]/70 p-6 hover:border-[#00F0FF]/30 transition-colors duration-500"
            >
              <div className="flex items-center gap-3">
                <span className="h-9 w-9 rounded-lg border border-[#00F0FF]/25 bg-[#00F0FF]/[0.07] text-[#00F0FF] flex items-center justify-center">
                  <c.icon size={16} />
                </span>
                <h3 className="font-display font-bold text-white">{c.title}</h3>
              </div>
              <ul className="mt-4 space-y-2">
                {c.lines.map((l) => (
                  <li key={l} className="text-[13px] text-[#9CA3AF] leading-relaxed">
                    {l}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
